type Route = '#/' | '#/firehose' | '#/frequencies' | '#/docs' | '#/faq' | '#/frequency';

export interface RouterOptions {
  onRouteChange: (hash: string) => void;
}

export function startRouter(opts: RouterOptions): () => void {
  const fire = (): void => {
    const hash = window.location.hash === '' ? '#/' : window.location.hash;
    opts.onRouteChange(hash);
  };

  window.addEventListener('hashchange', fire);
  fire();

  return () => window.removeEventListener('hashchange', fire);
}

export function isRoute(hash: string, route: Route): boolean {
  if (route === '#/frequency') return hash.startsWith('#/frequency/');
  const base = stripInnerAnchor(hash);
  return base === route || base.startsWith(`${route}/`);
}

export function parseFrequencyRoute(hash: string): string | null {
  const prefix = '#/frequency/';
  if (!hash.startsWith(prefix)) return null;
  const remainder = hash.slice(prefix.length);
  if (remainder === '') return null;
  try {
    return decodeURIComponent(remainder);
  } catch {
    return null;
  }
}

/**
 * For routes that support an in-page anchor (#/faq#section, #/docs/cli#flock-listen),
 * split into the route portion and the inner anchor. Returns the inner anchor or null.
 */
export function parseInnerAnchor(hash: string): string | null {
  // hash always starts with '#'. Strip leading '#', look for the next '#'.
  const rest = hash.startsWith('#') ? hash.slice(1) : hash;
  const second = rest.indexOf('#');
  if (second === -1) return null;
  const anchor = rest.slice(second + 1);
  if (anchor === '') return null;
  try {
    return decodeURIComponent(anchor);
  } catch {
    return anchor;
  }
}

export function stripInnerAnchor(hash: string): string {
  const rest = hash.startsWith('#') ? hash.slice(1) : hash;
  const second = rest.indexOf('#');
  if (second === -1) return hash;
  return '#' + rest.slice(0, second);
}

/**
 * #/docs/<slug> → slug. #/docs → '' (index). null if not a docs route.
 */
export function parseDocRoute(hash: string): string | null {
  const base = stripInnerAnchor(hash);
  if (base === '#/docs') return '';
  const prefix = '#/docs/';
  if (!base.startsWith(prefix)) return null;
  const slug = base.slice(prefix.length);
  if (slug === '') return '';
  if (!/^[a-z0-9-]+$/.test(slug)) return null;
  return slug;
}
